import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toDateKey } from '../../hooks/useReservations';

interface Props {
    /** The day currently shown in the rail, YYYY-MM-DD. */
    value: string;
    /** The button that opened this, so its own toggle is not read as an outside click. */
    anchor: React.RefObject<HTMLElement>;
    onPick: (dateKey: string) => void;
    onClose: () => void;
}

const icons = {
    prev: <svg viewBox='0 0 24 24' fill='none'><path d='m15 18-6-6 6-6' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    next: <svg viewBox='0 0 24 24' fill='none'><path d='m9 6 6 6-6 6' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /></svg>,
};

/** Monday-first, which is how a venue's week runs. */
const WEEKDAYS = Array.from({ length: 7 }, (_, i) => (
    // 1 Jan 2024 was a Monday, so this walks one real week for the locale's names.
    new Date(2024, 0, 1 + i).toLocaleDateString(undefined, { weekday: 'narrow' })
));

const noonOf = (dateKey: string): Date => new Date(`${dateKey}T12:00:00`);

/**
 * Month grid for jumping to a date the arrows would take all day to reach.
 *
 * Deliberately not `<input type="date">`: the native picker cannot be opened
 * reliably from our own button, and where it can it is a mouse-sized widget on a
 * screen people use with their thumbs.
 */
const DateJumper: React.FC<Props> = ({ value, anchor, onPick, onClose }) => {
    const [cursor, setCursor] = useState<Date>(() => {
        const d = noonOf(value);
        return new Date(d.getFullYear(), d.getMonth(), 1, 12);
    });
    const boxRef = useRef<HTMLDivElement>(null);
    const todayKey = toDateKey(new Date());

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        const onDown = (e: Event) => {
            const t = e.target as Node;
            if (boxRef.current?.contains(t) || anchor.current?.contains(t)) return;
            onClose();
        };
        window.addEventListener('keydown', onKey);
        document.addEventListener('pointerdown', onDown);
        return () => {
            window.removeEventListener('keydown', onKey);
            document.removeEventListener('pointerdown', onDown);
        };
    }, [anchor, onClose]);

    const { blanks, days } = useMemo(() => {
        const year = cursor.getFullYear();
        const month = cursor.getMonth();
        const first = new Date(year, month, 1, 12);
        const count = new Date(year, month + 1, 0).getDate();
        return {
            blanks: (first.getDay() + 6) % 7,
            days: Array.from({ length: count }, (_, i) => new Date(year, month, i + 1, 12)),
        };
    }, [cursor]);

    const stepMonth = (delta: number) => setCursor(
        c => new Date(c.getFullYear(), c.getMonth() + delta, 1, 12),
    );

    return (
        <div className='resv-jumper' ref={boxRef} role='dialog' aria-label='Pick a date'>
            <div className='resv-jumper__head'>
                <button type='button' className='resv-iconbtn' onClick={() => stepMonth(-1)} aria-label='Previous month'>
                    {icons.prev}
                </button>
                <span className='resv-jumper__month'>
                    {cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                </span>
                <button type='button' className='resv-iconbtn' onClick={() => stepMonth(1)} aria-label='Next month'>
                    {icons.next}
                </button>
            </div>

            <div className='resv-jumper__dows' aria-hidden='true'>
                {WEEKDAYS.map((d, i) => <span key={i}>{d}</span>)}
            </div>

            <div className='resv-jumper__grid'>
                {Array.from({ length: blanks }, (_, i) => <span key={`b${i}`} />)}
                {days.map(d => {
                    const key = toDateKey(d);
                    const classes = [
                        'resv-jumper__day',
                        key === value ? 'is-picked' : '',
                        key === todayKey ? 'is-today' : '',
                    ].filter(Boolean).join(' ');
                    return (
                        <button
                            key={key}
                            type='button'
                            className={classes}
                            onClick={() => { onPick(key); onClose(); }}
                            aria-current={key === value ? 'date' : undefined}
                        >
                            {d.getDate()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default DateJumper;
